// Service pour la catégorisation automatique des épisodes par mood et type de contenu
export class CategoryService {
  
  // Définition des catégories/moods disponibles
  static MOOD_CATEGORIES = {
    humour: {
      name: 'Humour',
      icon: '😂',
      keywords: ['humour', 'blague', 'drôle', 'comique', 'rire', 'marrant', 'rigolo', 'comédie', 'gag', 'sketch'],
      themes: ['humour', 'comédie']
    },
    culture: {
      name: 'Culture',
      icon: '🎭',
      keywords: ['culture', 'cinéma', 'film', 'série', 'livre', 'art', 'musique', 'spectacle', 'théâtre'],
      themes: ['culture', 'cinéma', 'musique', 'art', 'littérature']
    },
    actualites: {
      name: 'Actualités',
      icon: '📰',
      keywords: ['actualité', 'news', 'société', 'tendance', 'phénomène', 'réseaux sociaux', 'technologie'],
      themes: ['technologie', 'société', 'tendance']
    },
    personnel: {
      name: 'Personnel',
      icon: '💭',
      keywords: ['anecdote', 'personnel', 'souvenir', 'enfance', 'famille', 'obsession', 'habitude'],
      themes: ['personnel', 'enfance', 'famille']
    },
    jeux: {
      name: 'Jeux',
      icon: '🎮',
      keywords: ['jeu', 'gaming', 'vidéo', 'console', 'mobile', 'quiz', 'devinette'],
      themes: ['jeux', 'gaming']
    },
    special: {
      name: 'Spécial',
      icon: '⭐',
      keywords: ['halloween', 'noël', 'folklore', 'spécial', 'événement', 'fête'],
      themes: ['halloween', 'folklore', 'événement']
    }
  }

  // Types de contenu disponibles
  static CONTENT_TYPES = {
    withGames: {
      name: 'Avec jeux',
      icon: '🎯',
      description: 'Épisodes contenant des jeux ou activités'
    },
    withNews: {
      name: 'Actualités',
      icon: '📈',
      description: 'Épisodes riches en actualités'
    },
    withRecommendations: {
      name: 'Recommandations',
      icon: '💡',
      description: 'Épisodes avec recommandations culturelles'
    },
    longEpisodes: {
      name: 'Épisodes longs',
      icon: '⏰',
      description: 'Épisodes de plus de 60 minutes'
    },
    shortEpisodes: {
      name: 'Épisodes courts',
      icon: '⚡',
      description: 'Épisodes de moins de 30 minutes'
    },
    withNotableMoments: {
      name: 'Moments marquants',
      icon: '🌟',
      description: 'Épisodes avec beaucoup de moments notables'
    }
  }

  /**
   * Catégorise un épisode selon les moods définis
   * @param {Object} episode - L'épisode à catégoriser
   * @returns {Array} - Liste des catégories applicables
   */
  static categorizeByMood(episode) {
    if (!episode) return []

    const categories = []
    const searchableText = this.getSearchableText(episode)

    // Analyser chaque catégorie
    Object.entries(this.MOOD_CATEGORIES).forEach(([key, category]) => {
      let score = 0

      // Score basé sur les thèmes
      if (episode.themes && episode.themes.length > 0) {
        episode.themes.forEach(theme => {
          const themeNormalized = theme.toLowerCase()
          if (category.themes.some(t => themeNormalized.includes(t))) {
            score += 3
          }
        })
      }

      // Score basé sur les mots-clés dans le contenu
      category.keywords.forEach(keyword => {
        const regex = new RegExp(`\\b${keyword}\\b`, 'gi')
        const matches = searchableText.match(regex)
        if (matches) {
          score += matches.length
        }
      })

      // Seuil pour inclure la catégorie
      if (score >= 2) {
        categories.push({
          ...category,
          key,
          score
        })
      }
    })

    // Trier par score décroissant
    return categories.sort((a, b) => b.score - a.score)
  }

  /**
   * Détermine les types de contenu d'un épisode
   * @param {Object} episode - L'épisode à analyser
   * @returns {Array} - Liste des types de contenu applicables
   */
  static categorizeByContentType(episode) {
    if (!episode) return []

    const types = []

    // Avec jeux
    if (episode.games && episode.games.length > 0) {
      types.push({ ...this.CONTENT_TYPES.withGames, key: 'withGames' })
    }

    // Avec actualités (au moins 3 actualités)
    if (episode.introNews && episode.introNews.length >= 3) {
      types.push({ ...this.CONTENT_TYPES.withNews, key: 'withNews' })
    }

    // Avec recommandations (au moins 3 recommandations)
    if (episode.cultureRecommendations && episode.cultureRecommendations.length >= 3) {
      types.push({ ...this.CONTENT_TYPES.withRecommendations, key: 'withRecommendations' })
    }

    // Durée
    if (episode.duration) {
      const durationMinutes = episode.duration / 60
      if (durationMinutes > 60) {
        types.push({ ...this.CONTENT_TYPES.longEpisodes, key: 'longEpisodes' })
      } else if (durationMinutes < 30) {
        types.push({ ...this.CONTENT_TYPES.shortEpisodes, key: 'shortEpisodes' })
      }
    }

    // Avec moments marquants (au moins 2)
    if (episode.notableMoments && episode.notableMoments.length >= 2) {
      types.push({ ...this.CONTENT_TYPES.withNotableMoments, key: 'withNotableMoments' })
    }

    return types
  }

  /**
   * Extrait le texte recherchable d'un épisode
   * @param {Object} episode - L'épisode
   * @returns {string} - Texte concatené pour la recherche
   */
  static getSearchableText(episode) {
    let text = ''

    // Titre et résumé
    text += ` ${episode.title || ''}`
    text += ` ${episode.summary || ''}`

    // Actualités
    if (episode.introNews) {
      episode.introNews.forEach(news => {
        text += ` ${news.content || ''}`
      })
    }

    // Jeux
    if (episode.games) {
      episode.games.forEach(game => {
        text += ` ${game.content || ''}`
      })
    }

    // Moments notables
    if (episode.notableMoments) {
      episode.notableMoments.forEach(moment => {
        const content = typeof moment === 'string' ? moment : moment.content
        text += ` ${content || ''}`
      })
    }

    // Recommandations culturelles
    if (episode.cultureRecommendations) {
      episode.cultureRecommendations.forEach(reco => {
        const content = reco.type && reco.title ? `${reco.type} ${reco.title}` : (reco.content || String(reco))
        text += ` ${content || ''}`
      })
    }

    // Sections spécifiques à Podchiasse
    if (episode.annoyance_stories) {
      episode.annoyance_stories.forEach(story => {
        text += ` ${story.content || ''}`
      })
    }

    if (episode.farfie_moments) {
      episode.farfie_moments.forEach(moment => {
        text += ` ${moment.content || ''}`
      })
    }

    return text.toLowerCase()
  }

  /**
   * Recherche de contenu culturel spécifique
   * @param {Array} episodes - Liste des épisodes
   * @param {string} type - Type de contenu ('film', 'série', 'jeu', 'livre')
   * @param {string} query - Requête de recherche
   * @returns {Array} - Épisodes correspondants avec les recommandations trouvées
   */
  static searchCulturalContent(episodes, type, query = '') {
    if (!episodes || episodes.length === 0) return []

    const typePatterns = {
      film: /^(film|movie)\s*[:：]\s*/i,
      serie: /^(série|series|show)\s*[:：]\s*/i,
      jeu: /^(jeu|game|gaming)\s*[:：]\s*/i,
      livre: /^(livre|book|roman)\s*[:：]\s*/i,
      youtube: /^(chaîne\s+youtube|youtube|chaine)\s*[:：]\s*/i,
      podcast: /^(podcast)\s*[:：]\s*/i
    }

    const results = []

    episodes.forEach(episode => {
      if (!episode.cultureRecommendations) return

      const matchingRecommendations = episode.cultureRecommendations.filter(reco => {
        const content = typeof reco === 'string' ? reco : reco.content
        if (!content) return false

        // Vérifier le type
        const pattern = typePatterns[type]
        if (pattern && !pattern.test(content)) return false

        // Vérifier la requête si fournie
        if (query) {
          const searchText = content.toLowerCase()
          const queryWords = query.toLowerCase().split(' ')
          return queryWords.every(word => searchText.includes(word))
        }

        return true
      })

      if (matchingRecommendations.length > 0) {
        results.push({
          episode,
          recommendations: matchingRecommendations
        })
      }
    })

    return results
  }

  /**
   * Obtient toutes les catégories possibles pour l'interface
   * @returns {Object} - Objet contenant toutes les catégories
   */
  static getAllCategories() {
    return {
      moods: this.MOOD_CATEGORIES,
      contentTypes: this.CONTENT_TYPES
    }
  }

  /**
   * Filtre les épisodes par catégories sélectionnées
   * @param {Array} episodes - Liste des épisodes
   * @param {Array} selectedMoods - Moods sélectionnés
   * @param {Array} selectedContentTypes - Types de contenu sélectionnés
   * @returns {Array} - Épisodes filtrés
   */
  static filterByCategories(episodes, selectedMoods = [], selectedContentTypes = []) {
    if (!episodes || episodes.length === 0) return []
    if (selectedMoods.length === 0 && selectedContentTypes.length === 0) return episodes

    return episodes.filter(episode => {
      // Vérifier les moods
      if (selectedMoods.length > 0) {
        const episodeMoods = this.categorizeByMood(episode).map(m => m.key)
        const hasMood = selectedMoods.some(mood => episodeMoods.includes(mood))
        if (!hasMood) return false
      }

      // Vérifier les types de contenu
      if (selectedContentTypes.length > 0) {
        const episodeTypes = this.categorizeByContentType(episode).map(t => t.key)
        const hasType = selectedContentTypes.some(type => episodeTypes.includes(type))
        if (!hasType) return false
      }

      return true
    })
  }
}